import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { CreditCard, Banknote, Building2, Car, Pizza, ShoppingBag, Coffee, Fuel } from 'lucide-react';

const MagneticSynthesisAnimation = () => {
  const [pulledItems, setPulledItems] = useState([]);
  const [isPulling, setIsPulling] = useState(false);

  // Items with absolute positions spread across the box (percentages)
  const items = useMemo(() => [
    { id: 1, icon: Building2, label: 'בנק 1', color: '#FF90E8', top: '8%', left: '8%' },
    { id: 2, icon: Building2, label: 'בנק 2', color: '#4ECDC4', top: '12%', left: '78%' },
    { id: 3, icon: Building2, label: 'בנק 3', color: '#9B59B6', top: '38%', left: '85%' },
    { id: 4, icon: CreditCard, label: 'כרטיס 1', color: '#FFC900', top: '28%', left: '5%' },
    { id: 5, icon: CreditCard, label: 'כרטיס 2', color: '#FF6B6B', top: '5%', left: '42%' },
    { id: 6, icon: CreditCard, label: 'כרטיס 3', color: '#E74C3C', top: '52%', left: '82%' },
    { id: 7, icon: Banknote, label: 'מזומן', color: '#6BCB77', top: '50%', left: '8%' },
    { id: 8, icon: Pizza, label: 'אוכל', color: '#FFB347', top: '18%', left: '65%' },
    { id: 9, icon: Car, label: 'רכב', color: '#87CEEB', top: '35%', left: '22%' },
    { id: 10, icon: ShoppingBag, label: 'קניות', color: '#DDA0DD', top: '22%', left: '88%' },
    { id: 11, icon: Coffee, label: 'קפה', color: '#D2691E', top: '5%', left: '22%' },
    { id: 12, icon: Fuel, label: 'דלק', color: '#708090', top: '45%', left: '35%' },
  ], []);

  // Animation cycle - pull items one by one
  useEffect(() => {
    let timeouts = [];
    
    const startPulling = () => {
      setIsPulling(true);
      setPulledItems([]);
      
      // Pull each item one by one with delay
      items.forEach((item, index) => {
        const timeout = setTimeout(() => {
          setPulledItems(prev => [...prev, item.id]);
        }, index * 600); // 600ms between each pull (3x slower)
        timeouts.push(timeout);
      });
      
      // Reset after all items pulled
      const resetTimeout = setTimeout(() => {
        setIsPulling(false);
        setPulledItems([]);
      }, items.length * 600 + 3000);
      timeouts.push(resetTimeout);
    };

    // Initial delay then start
    const initialTimeout = setTimeout(startPulling, 1500);
    timeouts.push(initialTimeout);
    
    // Loop
    const interval = setInterval(startPulling, 12000);

    return () => {
      timeouts.forEach(t => clearTimeout(t));
      clearInterval(interval);
    };
  }, [items]);

  // Floating animation for idle state
  const getFloatAnimation = (index) => ({
    y: [0, -6, 0, 6, 0],
    rotate: [0, 2, 0, -2, 0],
    transition: {
      duration: 3 + (index % 3),
      repeat: Infinity,
      ease: "easeInOut",
    }
  });

  // Pull transition
  const pullTransition = {
    type: "spring",
    stiffness: 150,
    damping: 15,
  };

  return (
    <div className="w-full aspect-video bg-background-light flex flex-col items-center justify-end font-sans overflow-hidden rounded-2xl border-4 border-black relative">
      
      {/* Floating Items */}
      {items.map((item, index) => {
        const isPulled = pulledItems.includes(item.id);
        
        return (
          <motion.div
            key={item.id}
            className="absolute flex flex-col items-center gap-1 z-10"
            style={{ 
              top: item.top,
              left: item.left,
            }}
            animate={isPulled 
              ? { 
                  top: '78%',
                  left: '50%',
                  scale: 0.5,
                  opacity: 1,
                  x: '-50%',
                  zIndex: 5,
                }
              : getFloatAnimation(index)
            }
            transition={isPulled ? pullTransition : undefined}
          >
            <div 
              className="p-2 rounded-lg border-2 border-black shadow-hard-sm"
              style={{ backgroundColor: item.color }}
            >
              <item.icon size={18} className="text-black" />
            </div>
            <span className="text-[10px] font-bold text-gray-700 whitespace-nowrap">{item.label}</span>
          </motion.div>
        );
      })}

      {/* Magnet emoji at bottom */}
      <motion.div 
        className="relative z-20 mb-6 flex flex-col items-center"
        animate={isPulling ? { scale: [1, 1.15, 1] } : { scale: 1 }}
        transition={{ duration: 0.4, repeat: isPulling ? Infinity : 0 }}
      >
        <div className="flex items-center gap-1">
          <motion.span 
            className="text-base md:text-2xl"
            animate={isPulling ? { opacity: [0.5, 1, 0.5], scale: [0.9, 1.1, 0.9] } : { opacity: 0.7 }}
            transition={{ duration: 0.3, repeat: isPulling ? Infinity : 0 }}
          >
            ⚡
          </motion.span>
          <span className="text-3xl md:text-6xl">🧲</span>
          <motion.span 
            className="text-base md:text-2xl"
            animate={isPulling ? { opacity: [0.5, 1, 0.5], scale: [0.9, 1.1, 0.9] } : { opacity: 0.7 }}
            transition={{ duration: 0.3, repeat: isPulling ? Infinity : 0, delay: 0.15 }}
          >
            ⚡
          </motion.span>
        </div>
        <motion.p 
          className="text-center text-sm font-bold mt-1 text-gray-700"
          animate={{ opacity: isPulling ? 1 : 0.7 }}
        >
          {isPulling ? 'הכל במקום אחד' : 'הכל במקום אחד'}
        </motion.p>
      </motion.div>
    </div>
  );
};

export default MagneticSynthesisAnimation;
